// Video watch time (api-phase1-telephony-audit.md, "Video watch time").
//
// Every player that shows a video session calls useWatchTracker(descriptor.viewId) and reports, through
// setPlaying(), whether the picture is actually playing. The tracker counts only the milliseconds when the
// video plays AND the page is visible AND the player is on screen (pre-staged players are hidden), and
// reports them:
//   - POST /api/video/views/:viewId/heartbeat { watchedMs }  every 15 s (the delta since the last report)
//   - POST /api/video/views/:viewId/end { watchedMs, reason }
//       switched : the player moved to another session (camera, mode or position changed)
//       closed   : the player unmounted (drawer closed, tile removed, screen left)
//       hidden   : the page stayed hidden longer than HIDDEN_GRACE_MS  → keepalive fetch
//       unload   : the page is being unloaded (pagehide)              → keepalive fetch
// A hidden page stops counting at once, but keeps its view (and the player its stream) for HIDDEN_GRACE_MS: Edge
// and Chrome on Windows report a window as hidden while another window covers it (the camera matrix on top, an
// alt-tab), and ending the view there restarted every stream when it came back (staging, 2026-10-29). Heartbeats go
// on meanwhile (0 ms watched), so the server keeps the view. An ended view never comes back. When the page is shown
// again after the view ended (or restored from the back/forward cache), or the server says the view is gone (it ends
// silent views after 45 s), the tracker calls onRenew() so the player asks for a fresh session, which brings a new
// viewId.

import { useCallback, useEffect, useRef } from 'react';
import { api, ApiError, type ViewEndReason } from '../api.ts';

export const HEARTBEAT_MS = 15_000;
/** How long a hidden page keeps its view before ending it (under the server's 45 s, with a heartbeat in between). */
export const HIDDEN_GRACE_MS = 20_000;

/** Statuses on a heartbeat that mean the view no longer exists on the server. */
const GONE = new Set([404, 409, 410]);

const visibleNow = () => typeof document === 'undefined' || document.visibilityState === 'visible';
const clock = () => performance.now();

export class WatchTracker {
  constructor(onScreen: boolean) {
    this.onScreen = onScreen;
  }
  viewId: string | null = null;
  playing = false;
  visible = visibleNow();
  /** False for players rendered off screen (offer pre-staging): their time is never counted. */
  onScreen = true;
  /** Counted but not yet reported. */
  private acc = 0;
  /** Start of the running counting segment, or null while not counting. */
  private since: number | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  /** Ends the view if the page is still hidden when it fires. */
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  /** Views this tracker has ended (they may still be on screen until the new session arrives). */
  private ended = new Set<string>();
  /** The page was hidden / frozen and the view ended: renew when it comes back. */
  needsRenew = false;
  onRenew: (() => void) | null = null;

  private counting(): boolean {
    return this.viewId !== null && this.playing && this.visible && this.onScreen;
  }
  private settle(now: number): void {
    if (this.since !== null) {
      this.acc += now - this.since;
      this.since = null;
    }
  }
  private resume(now: number): void {
    if (this.since === null && this.counting()) this.since = now;
  }
  /** Applies a state change without losing the time counted so far. */
  private change(fn: () => void): void {
    const now = clock();
    this.settle(now);
    fn();
    this.resume(now);
  }
  /** Takes the unreported milliseconds (whole ms), keeping the segment running. */
  private take(): number {
    const now = clock();
    this.settle(now);
    const ms = Math.max(0, Math.round(this.acc));
    this.acc = 0;
    this.resume(now);
    return ms;
  }

  setPlaying(p: boolean): void {
    if (p !== this.playing) this.change(() => (this.playing = p));
  }

  setOnScreen(v: boolean): void {
    if (v !== this.onScreen) this.change(() => (this.onScreen = v));
  }

  begin(viewId: string): void {
    if (this.viewId === viewId || this.ended.has(viewId)) return;
    if (this.viewId) this.finish('switched');
    this.change(() => {
      this.viewId = viewId;
      this.acc = 0;
    });
    this.timer = setInterval(() => this.heartbeat(), HEARTBEAT_MS);
  }

  /** Ends the current view. keepalive: the page may be going away. */
  finish(reason: ViewEndReason, keepalive = false): void {
    const id = this.viewId;
    if (!id) return;
    const ms = this.take();
    this.change(() => (this.viewId = null));
    this.acc = 0;
    this.stopTimer();
    this.ended.add(id);
    if (keepalive) api.viewEndKeepalive(id, ms, reason);
    else api.viewEnd(id, ms, reason).catch(() => undefined);
  }

  private heartbeat(): void {
    const id = this.viewId;
    if (!id) return;
    const ms = this.take();
    api.viewHeartbeat(id, ms).catch((e: unknown) => {
      if (e instanceof ApiError && GONE.has(e.status)) {
        // The server ended this view (e.g. no heartbeat for 45 s while the machine slept): start a new one.
        if (this.viewId === id) {
          this.change(() => (this.viewId = null));
          this.acc = 0;
          this.stopTimer();
          this.ended.add(id);
          this.onRenew?.();
        }
      } else if (this.viewId === id) {
        this.acc += ms; // network blip: report it with the next heartbeat
      }
    });
  }

  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  onVisibility(): void {
    const v = visibleNow();
    if (v === this.visible) return;
    if (!v) {
      // Not watched from now on; the view (and the stream) stay for a while in case the page comes straight back.
      this.change(() => (this.visible = false));
      this.clearHidden();
      this.hiddenTimer = setTimeout(() => {
        this.hiddenTimer = null;
        if (this.visible || !this.viewId) return;
        this.finish('hidden', true);
        this.needsRenew = true;
      }, HIDDEN_GRACE_MS);
      return;
    }
    this.clearHidden();
    this.change(() => (this.visible = true));
    if (this.needsRenew) {
      this.needsRenew = false;
      this.onRenew?.();
    }
  }

  private clearHidden(): void {
    if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
    this.hiddenTimer = null;
  }

  onPageHide(persisted: boolean): void {
    if (this.viewId) {
      this.finish('unload', true);
      if (persisted) this.needsRenew = true;
    }
  }

  onPageShow(persisted: boolean): void {
    this.change(() => (this.visible = visibleNow()));
    if (persisted && this.needsRenew && this.visible) {
      this.needsRenew = false;
      this.onRenew?.();
    }
  }

  dispose(): void {
    this.stopTimer();
    this.clearHidden();
  }
}

export interface WatchTrackerHandle {
  /** Report whether the picture is actually playing (not paused, buffering, failed or blank). */
  setPlaying: (playing: boolean) => void;
}

/**
 * Tracks watch time for one player. Pass the current session's viewId (null while there is none).
 * onRenew is called when the player should request a fresh session (page shown again after hidden,
 * or the server ended the view). onScreen false (a hidden pre-staged player) keeps the view open but
 * counts none of its time as watched.
 */
export function useWatchTracker(viewId: string | null | undefined, onRenew?: () => void, onScreen = true): WatchTrackerHandle {
  const ref = useRef<WatchTracker | null>(null);
  if (!ref.current) ref.current = new WatchTracker(onScreen);
  const t = ref.current;
  t.onRenew = onRenew ?? null;
  useEffect(() => t.setOnScreen(onScreen), [t, onScreen]);

  // Declared before the viewId effect: on unmount React runs cleanups in declaration order, so this
  // flag is set before the viewId cleanup decides between `closed` and `switched`.
  const unmounting = useRef(false);
  useEffect(() => {
    unmounting.current = false;
    return () => {
      unmounting.current = true;
    };
  }, []);

  useEffect(() => {
    if (!viewId) return;
    t.begin(viewId);
    return () => t.finish(unmounting.current ? 'closed' : 'switched');
  }, [t, viewId]);

  useEffect(() => {
    const vis = () => t.onVisibility();
    const hide = (e: PageTransitionEvent) => t.onPageHide(e.persisted);
    const show = (e: PageTransitionEvent) => t.onPageShow(e.persisted);
    document.addEventListener('visibilitychange', vis);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', show);
    return () => {
      document.removeEventListener('visibilitychange', vis);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', show);
      t.dispose();
    };
  }, [t]);

  const setPlaying = useCallback((p: boolean) => t.setPlaying(p), [t]);
  return { setPlaying };
}
